// src/pages/dashboard/branch/analytics/ItemsAnalyticsTab.tsx
import React, { useEffect, useState, useMemo, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiPackage,
    FiTrendingUp,
    FiClock,
    FiMousePointer,
    FiChevronDown,
    FiAward,
    FiBarChart2,
    FiCalendar,
} from "react-icons/fi";
import Card from "../../../../components/ui/Card";
import Skeleton from "../../../../components/ui/Skeleton";
import PaginationWithLimitSelector from "../../../../components/ui/PaginationWithLimitSelector";
import { analyticsService } from "../../../../services/analyticsService";
import { BranchInsightsItem } from "../../../../types/analytics";
import { useCachedCategories, useCachedMenuItems } from "../../../../hooks/useCachedData";
import { useLocalizedMenuItem } from "../../../../hooks/useLocalizedMenuItem";
import { getCategoryName } from "../../../../utils/categoryHelper";
import { MenuItem } from "../../../../utils/supabaseClient";
import { useAnalyticsContext, convertBranchStartDateToDate } from "../../../../context/AnalyticsContext";

interface ItemsAnalyticsTabProps {
    branchId: string;
}

// Generate years for dropdown (from branch start year to current year)
const generateYears = (branchStartDate: Date | null | undefined, t: any) => {
    const currentYear = new Date().getFullYear();
    const startYear = branchStartDate ? branchStartDate.getFullYear() : currentYear - 1;
    const years: { value: number | null; label: string }[] = [
        { value: null, label: t("analytics.allYears", "All Years") },
    ];
    for (let i = currentYear; i >= startYear; i--) {
        years.push({ value: i, label: i.toString() });
    }
    return years;
};

// Generate months for dropdown
const generateMonths = (selectedYear: number | null, branchStartDate: Date | null | undefined, t: any) => {
    const allMonths = [
        { value: 1, label: t("analytics.months.january", "January") },
        { value: 2, label: t("analytics.months.february", "February") },
        { value: 3, label: t("analytics.months.march", "March") },
        { value: 4, label: t("analytics.months.april", "April") },
        { value: 5, label: t("analytics.months.may", "May") },
        { value: 6, label: t("analytics.months.june", "June") },
        { value: 7, label: t("analytics.months.july", "July") },
        { value: 8, label: t("analytics.months.august", "August") },
        { value: 9, label: t("analytics.months.september", "September") },
        { value: 10, label: t("analytics.months.october", "October") },
        { value: 11, label: t("analytics.months.november", "November") },
        { value: 12, label: t("analytics.months.december", "December") },
    ];

    const months: { value: number | null; label: string }[] = [
        { value: null, label: t("analytics.allMonths", "All Months") },
    ];

    if (!selectedYear) return months;

    const startMonth = 1;
    const endMonth = 12;

    // If selected year is the branch start year, only show months from branch start month onwards
    if (branchStartDate && selectedYear === branchStartDate.getFullYear()) {
        const branchStartMonth = branchStartDate.getMonth() + 1; // getMonth() returns 0-11
        for (let month = branchStartMonth; month <= endMonth; month++) {
            const monthObj = allMonths.find(m => m.value === month);
            if (monthObj) {
                months.push(monthObj);
            }
        }
    } else {
        // For other years, show all months
        months.push(...allMonths);
    }

    return months;
};

// Custom Dropdown Component
const CustomDropdown: React.FC<{
    value: number | string | null;
    options: { value: number | string | null; label: string }[];
    onChange: (value: any) => void;
    icon?: React.ReactNode;
    placeholder?: string;
    disabled?: boolean;
}> = ({ value, options, onChange, icon, placeholder, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = options.find((opt) => opt.value === value);

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`
          flex items-center gap-2 px-4 py-2.5 rounded-xl
          bg-white dark:bg-gray-800 
          border border-gray-200 dark:border-gray-700
          hover:border-primary/50 dark:hover:border-primary/50
          focus:outline-none focus:ring-2 focus:ring-primary/20
          transition-all duration-200
          min-w-[160px] justify-between
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          ${isOpen ? "ring-2 ring-primary/20 border-primary/50" : ""}
        `}
            >
                <div className="flex items-center gap-2">
                    {icon && <span className="text-gray-400">{icon}</span>}
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {selectedOption?.label || placeholder}
                    </span>
                </div>
                <FiChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                        }`}
                />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 z-10"
                            onClick={() => setIsOpen(false)}
                        />

                        {/* Dropdown Menu */}
                        <motion.div
                            initial={{ opacity: 0, y: -10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.95 }}
                            transition={{ duration: 0.15 }}
                            className="absolute z-20 mt-2 w-full min-w-[180px] py-1 
                bg-white dark:bg-gray-800 
                border border-gray-200 dark:border-gray-700 
                rounded-xl shadow-lg 
                max-h-60 overflow-auto
                scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600"
                        >
                            {options.map((option) => (
                                <button
                                    key={String(option.value)}
                                    onClick={() => {
                                        onChange(option.value);
                                        setIsOpen(false);
                                    }}
                                    className={`
                    w-full px-4 py-2.5 text-sm
                    transition-colors duration-150
                    ${value === option.value
                                            ? "bg-primary/10 text-primary font-medium"
                                            : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                        }
                    ${option.value === null ? "text-center font-semibold" : "text-start"}
                  `}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

// Top Item Card Component - Memoized to prevent re-renders
const TopItemCard = memo<{
    item: BranchInsightsItem;
    rank: number;
    isLoading?: boolean;
    branchId: string;
}>(({ item, rank, isLoading, branchId }) => {
    const getRankStyle = (rank: number) => {
        switch (rank) {
            case 1:
                return {
                    bg: "from-amber-400 to-yellow-500",
                    border: "border-amber-300",
                    badge: "bg-amber-500",
                    icon: "🥇",
                };
            case 2:
                return {
                    bg: "from-gray-300 to-gray-400",
                    border: "border-gray-300",
                    badge: "bg-gray-400",
                    icon: "🥈",
                };
            case 3:
                return {
                    bg: "from-orange-400 to-orange-500",
                    border: "border-orange-300",
                    badge: "bg-orange-500",
                    icon: "🥉",
                };
            default:
                return {
                    bg: "from-primary to-primary-dark",
                    border: "border-primary",
                    badge: "bg-primary",
                    icon: `#${rank}`,
                };
        }
    };

    const { t, i18n } = useTranslation();
    const { localizedName } = useLocalizedMenuItem(branchId, item.item_id);

    const style = getRankStyle(rank);
    

    if (isLoading) {
        return (
            <div className="relative p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                <Skeleton height="h-6" width="w-8" className="absolute top-3 end-3" />
                <Skeleton height="h-5" width="w-3/4" className="mb-3" />
                <div className="grid grid-cols-3 gap-3">
                    <Skeleton height="h-10" width="w-full" />
                    <Skeleton height="h-10" width="w-full" />
                    <Skeleton height="h-10" width="w-full" />
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: rank * 0.1 }}
            className={`
        relative p-5 rounded-2xl 
        bg-white dark:bg-gray-800 
        border-2 ${style.border} dark:border-opacity-30
        shadow-sm hover:shadow-md transition-shadow duration-300
        overflow-hidden
      `}
        >
            {/* Rank Badge */}
            <div className="absolute top-3 end-3 text-2xl">{style.icon}</div>

            {/* Item Name */}
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pe-10 truncate">
                {localizedName || item.name}
            </h4>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                    <div className="text-lg font-bold text-primary">{item.clicks}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t("analytics.clicks")}</div>
                </div>
                <div className="text-center p-2 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                    <div className="text-lg font-bold text-emerald-500">
                        {item.time_spents.toFixed(0)}s
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t("analytics.timeSpent")}</div>
                </div>
                <div className="text-center p-2 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                    <div className="text-lg font-bold text-blue-500">
                        {item.item_average.toFixed(1)}s
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t("analytics.avgTime")}</div>
                </div>
            </div>
        </motion.div>
    );
});
TopItemCard.displayName = 'TopItemCard';

// Stats Card Component - Memoized to prevent re-renders
const StatsCard = memo<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    color: string;
    isLoading?: boolean;
}>(({ title, value, subtitle, icon, color, isLoading }) => {
    if (isLoading) {
        return (
            <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                <div className="flex items-start justify-between">
                    <Skeleton height="h-12" width="w-12" rounded="rounded-xl" />
                    <div className="flex-1 ms-4">
                        <Skeleton height="h-4" width="w-20" className="mb-2" />
                        <Skeleton height="h-8" width="w-24" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm"
        >
            <div className="flex items-start justify-between">
                <div
                    className={`p-3 rounded-xl bg-gradient-to-br ${color} bg-opacity-10`}
                >
                    <span className="text-white text-xl">{icon}</span>
                </div>
                <div className="text-end">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {value}
                    </p>
                    {subtitle && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
        </motion.div>
    );
});
StatsCard.displayName = 'StatsCard';

// Item Row Component - Memoized to prevent re-renders
const ItemRow = memo<{
    item: BranchInsightsItem;
    index: number;
    maxClicks: number;
    branchId: string;
}>(({ item, index, maxClicks, branchId }) => {
    const { t } = useTranslation();
    const { localizedName, menuItem } = useLocalizedMenuItem(branchId, item.item_id);
    const clickPercentage = (item.clicks / maxClicks) * 100;

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.03 }}
            className="group relative px-3 sm:px-6 py-3 sm:py-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 hover:border-primary/30 dark:hover:border-primary/30 hover:shadow-md transition-all duration-300"
        >
            {/* Progress Bar Background */}
            <div
                className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/5 to-transparent"
                style={{ width: `${clickPercentage}%` }}
            />

            <div className="relative z-10">
                {/* Mobile Layout */}
                <div className="flex flex-col gap-3 md:hidden">
                    {/* Top row: Rank, Image, Name */}
                    <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300 flex-shrink-0">
                            {index + 1}
                        </div>

                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 border border-gray-200 dark:border-gray-600">
                            {menuItem?.image_url ? (
                                <img
                                    src={menuItem.image_url}
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <FiPackage className="w-4 h-4 text-gray-400" />
                                </div>
                            )}
                        </div>

                        <h4 className="flex-1 text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary transition-colors">
                            {localizedName || item.name}
                        </h4>
                    </div>

                    {/* Bottom row: Stats */}
                    <div className="grid grid-cols-3 gap-2">
                        <div className="text-center p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                            <div className="flex items-center justify-center gap-1">
                                <FiMousePointer className="w-3.5 h-3.5 text-orange-500" />
                                <span className="text-sm font-bold text-orange-500">{item.clicks}</span>
                            </div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{t("analytics.clicks")}</div>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                            <div className="flex items-center justify-center gap-1">
                                <FiClock className="w-3.5 h-3.5 text-emerald-500" />
                                <span className="text-sm font-bold text-emerald-500">
                                    {item.time_spents >= 60
                                        ? `${(item.time_spents / 60).toFixed(1)}m`
                                        : `${item.time_spents.toFixed(0)}s`}
                                </span>
                            </div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{t("analytics.totalTimeSpent")}</div>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                            <span className="text-sm font-bold text-blue-500">{item.item_average.toFixed(1)}s</span>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{t("analytics.avgTime")}</div>
                        </div>
                    </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 lg:gap-6 items-center">
                    {/* Rank, Image, and Name - First Column (6 cols) */}
                    <div className="col-span-6 flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300 flex-shrink-0">
                            {index + 1}
                        </div>

                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 border border-gray-200 dark:border-gray-600">
                            {menuItem?.image_url ? (
                                <img
                                    src={menuItem.image_url}
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <FiPackage className="w-5 h-5 text-gray-400" />
                                </div>
                            )}
                        </div>

                        <div className="min-w-0 flex-1">
                            <h4 className="text-base font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary transition-colors">
                                {localizedName || item.name}
                            </h4>
                        </div>
                    </div>

                    {/* Clicks - Middle Left (2 cols) */}
                    <div className="col-span-2 flex items-center justify-center">
                        <div className="text-center w-full">
                            <div className="flex items-center justify-center gap-1.5 mb-1.5">
                                <FiMousePointer className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                <span className="text-lg font-bold text-orange-500">{item.clicks}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t("analytics.clicks")}</div>
                        </div>
                    </div>

                    {/* Total Time - Middle Right (2 cols) */}
                    <div className="col-span-2 flex items-center justify-center">
                        <div className="text-center w-full">
                            <div className="flex items-center justify-center gap-1.5 mb-1.5">
                                <FiClock className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                <span className="text-lg font-bold text-emerald-500">
                                    {item.time_spents >= 60
                                        ? `${(item.time_spents / 60).toFixed(1)}m`
                                        : `${item.time_spents.toFixed(0)}s`}
                                </span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{t("analytics.totalTimeSpent")}</div>
                        </div>
                    </div>

                    {/* Average Time - Right (2 cols) */}
                    <div className="col-span-2 flex items-center justify-center">
                        <div className="text-center w-full">
                            <div className="flex items-center justify-center gap-1.5 mb-1.5">
                                <span className="text-lg font-bold text-blue-500">
                                    {item.item_average.toFixed(1)}s
                                </span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 leading-tight">{t("analytics.avgTime")}</div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
});
ItemRow.displayName = 'ItemRow';
const ItemsAnalyticsTab: React.FC<ItemsAnalyticsTabProps> = ({ branchId }) => {
    const { t, i18n } = useTranslation();
    const { branchStartDate: contextBranchStartDate } = useAnalyticsContext();
    
    // Convert branch start date
    const branchStartDateObj = convertBranchStartDateToDate(contextBranchStartDate);

    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState<BranchInsightsItem[]>([]);
    const [topItems, setTopItems] = useState<BranchInsightsItem[]>([]);
    const [totalClicks, setTotalClicks] = useState(0);
    const [totalTimeSpent, setTotalTimeSpent] = useState(0);
    const [totalAverage, setTotalAverage] = useState(0);

    // Filters
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    // Get cached categories
    const { categories } = useCachedCategories({ branchId });

    // Get cached menu items for images
    const { menuItems: cachedMenuItems } = useCachedMenuItems({ branchId });

    // Create a lookup map for menu items by ID
    const menuItemsMap = useMemo(() => {
        const map = new Map<string, MenuItem>();
        cachedMenuItems.forEach((item) => {
            map.set(item.id, item);
        });
        return map;
    }, [cachedMenuItems]);

    // Generate category options
    const categoryOptions = useMemo(() => {
        const options: { value: string | null; label: string }[] = [
            { value: null, label: t("analytics.allCategories", "All Categories") },
        ];
        categories.forEach((cat) => {
            options.push({
                value: cat.id,
                label: getCategoryName(cat, i18n.language),
            });
        });
        return options;
    }, [categories, i18n.language, t]);

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await analyticsService.getBranchInsights(
                    [{ action: "item_insights" }],
                    [
                        {
                            branch_id: branchId,
                            category_id: selectedCategory,
                            year: selectedYear,
                            month: selectedMonth,
                            limit: itemsPerPage,
                            offset: (currentPage - 1) * itemsPerPage,
                        },
                    ]
                );

                if (response && response.length > 0) {
                    // The response is a direct array: [{ items, top_items, total_clicks, ... }]
                    // Not wrapped in branch_insights
                    const data = response[0] as any;

                    // Check if data has the expected structure
                    if (data) {
                        // Handle both possible structures
                        const insightsData = data.branch_insights?.[0] || data;

                        setItems(insightsData.items || []);
                        setTopItems(insightsData.top_items || []);
                        setTotalClicks(insightsData.total_clicks || 0);
                        setTotalTimeSpent(insightsData.total_time_spents || 0);
                        setTotalAverage(insightsData.total_average || 0);
                        // Estimate total items for pagination
                        const itemsCount = insightsData.items?.length || 0;
                        setTotalItems(
                            itemsCount === itemsPerPage
                                ? currentPage * itemsPerPage + 1
                                : (currentPage - 1) * itemsPerPage + itemsCount
                        );
                    }
                }
            } catch (error) {
                console.error("Error fetching item insights:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [branchId, selectedYear, selectedMonth, selectedCategory, currentPage, itemsPerPage]);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedYear, selectedMonth, selectedCategory]);

    // Clear month selection if it becomes invalid when year changes
    useEffect(() => {
        if (selectedYear && selectedMonth && branchStartDateObj) {
            const validMonths = generateMonths(selectedYear, branchStartDateObj, t);
            const isMonthValid = validMonths.some(m => m.value === selectedMonth);
            if (!isMonthValid) {
                setSelectedMonth(null);
            }
        }
    }, [selectedYear, branchStartDateObj, t]);

    const years = generateYears(branchStartDateObj, t);
    const months = generateMonths(selectedYear, branchStartDateObj, t);
    const maxClicks = Math.max(...items.map((i) => i.clicks), 1);

    return (
        <div className="space-y-6">
            <div className="flex gap-4 flex-wrap justify-center">
                <CustomDropdown
                    value={selectedYear}
                    options={years}
                    onChange={setSelectedYear}
                    icon={<FiTrendingUp className="w-4 h-4" />}
                    placeholder={t("analytics.selectYear", "Select Year")}
                />
                <CustomDropdown
                    value={selectedMonth}
                    options={months}
                    onChange={setSelectedMonth}
                    disabled={!selectedYear}
                    icon={<FiCalendar className="w-4 h-4" />}
                    placeholder={t("analytics.selectMonth", "Select Month")}
                />
                <CustomDropdown
                    value={selectedCategory}
                    options={categoryOptions}
                    onChange={setSelectedCategory}
                    icon={<FiPackage className="w-4 h-4" />}
                    placeholder={t("analytics.categories")}
                />
            </div>
            {/* Filters beneath stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">





            </div>
            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatsCard
                    title={t("analytics.totalClicks", "Total Clicks")}
                    value={totalClicks.toLocaleString()}
                    icon={<FiMousePointer />}
                    color="from-primary to-primary-dark"
                    isLoading={isLoading}
                />
                <StatsCard
                    title={t("analytics.totalTimeSpent", "Total Time Spent")}
                    value={`${(totalTimeSpent / 60).toFixed(1)}m`}
                    subtitle={`${totalTimeSpent.toFixed(0)} seconds`}
                    icon={<FiClock />}
                    color="from-emerald-500 to-emerald-600"
                    isLoading={isLoading}
                />
                <StatsCard
                    title={t("analytics.avgTimePerClick", "Avg. Time / Click")}
                    value={`${totalAverage.toFixed(1)}s`}
                    icon={<FiTrendingUp />}
                    color="from-blue-500 to-blue-600"
                    isLoading={isLoading}
                />
            </div>



            {/* Top 3 Items */}
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <FiAward className="w-5 h-5 text-amber-500" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t("analytics.topPerformers", "Top Performers")}
                    </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {isLoading ? (
                        <>
                            <TopItemCard item={{} as any} rank={1} isLoading branchId={branchId} />
                            <TopItemCard item={{} as any} rank={2} isLoading branchId={branchId} />
                            <TopItemCard item={{} as any} rank={3} isLoading branchId={branchId} />
                        </>
                    ) : topItems.length > 0 ? (
                        topItems.map((item, idx) => (
                            <TopItemCard key={item.item_id} item={item} rank={idx + 1} branchId={branchId} />
                        ))
                    ) : (
                        <div className="col-span-3 text-center py-8 text-gray-500 dark:text-gray-400">
                            {t("analytics.noTopItems", "No top items found for the selected period")}
                        </div>
                    )}
                </div>
            </div>

            {/* Items List */}
            <Card>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <FiPackage className="w-5 h-5 text-primary" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {t("analytics.allItems", "All Items")}
                        </h3>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t("analytics.sortedByClicks", "Sorted by clicks")}
                    </span>
                </div>

                {isLoading ? (
                    <div className="space-y-3">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800">
                                <div className="flex items-center gap-4">
                                    <Skeleton height="h-8" width="w-8" rounded="rounded-full" />
                                    <Skeleton height="h-4" width="w-1/3" />
                                    <div className="ms-auto flex gap-6">
                                        <Skeleton height="h-6" width="w-16" />
                                        <Skeleton height="h-6" width="w-16" />
                                        <Skeleton height="h-6" width="w-16" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : items.length > 0 ? (
                    <div className="space-y-3">
                        {items.map((item, idx) => (
                            <ItemRow
                                key={item.item_id}
                                item={item}
                                index={idx}
                                maxClicks={maxClicks}
                                branchId={branchId}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <FiPackage className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                            {t("analytics.noItems", "No items found for the selected filters")}
                        </p>
                    </div>
                )}

                {/* Pagination */}
                {totalItems > itemsPerPage && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <PaginationWithLimitSelector
                            currentPage={currentPage}
                            totalPages={Math.ceil(totalItems / itemsPerPage)}
                            totalItems={totalItems}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                            onLimitChange={(limit) => {
                                setItemsPerPage(limit);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                )}
            </Card>
        </div>
    );
};

export default ItemsAnalyticsTab;
